/*
  Wishlist drawer (sections/wishlist-slideout.liquid) and the saved-items count on
  the header heart. Ported from Long's wishlist-slideout branch (May 2026).

  - Tapping the heart opens the drawer. Saving an item does NOT open it (decided
    7 Oct 2026): on PLPs, and in the BFCM wishlist-only collections, shoppers save
    several items in a row, so the heart's count ticks up instead.
  - Cards are fetched fresh on every open, so stock and price are never older than
    the drawer itself (stale stock is what made Add All fail).
  - Adding to cart, one card or all of them, goes through MNWishlist.addAllToCart
    (assets/wishlist-add-all.js). An item only leaves the wishlist once Shopify has
    actually added it.
*/
(() => {
  if (customElements.get('wishlist-drawer')) return;

  const SEARCH_BATCH = 50;
  const WISHLIST_TIMEOUT_MS = 8000;
  const SIZE_MAP = { XXXXXL: '5XL', XXXXL: '4XL', XXXL: '3XL', XXL: '2XL', XXXS: '3XS', XXS: '2XS' };

  // XXS -> 2XS, as src/helpers/formatSize.ts does for the wishlist page.
  function formatSize(title) {
    if (!title) return '';
    const separator = title.includes(' / ') ? ' / ' : title.includes(' - ') ? ' - ' : null;
    const parts = separator ? title.split(separator) : [title];
    return parts.map((part) => SIZE_MAP[part.trim().toUpperCase()] || part.trim()).join(separator || '');
  }

  function escapeHTML(value) {
    return String(value == null ? '' : value).replace(
      /[&<>"']/g,
      (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]
    );
  }

  function isWishlistPage() {
    return /\/pages\/wishlist\/?$/.test(window.location.pathname);
  }

  // ---------------------------------------------------------------------------
  // Wishlist (assets/wishlist-store.js)
  // ---------------------------------------------------------------------------

  // Saved items with an id (optimistic adds still in flight are left out, as
  // they can't be removed yet).
  function savedItems(store) {
    return store.items().filter((item) => item.itemId);
  }

  // ---------------------------------------------------------------------------
  // Count on the header heart (sections/navigation.liquid, [data-wishlist-count])
  // ---------------------------------------------------------------------------
  function setSavedCount(n) {
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = n > 0 ? String(n) : '';
    });
    const icon = document.getElementById('wishlist-icon-bubble');
    if (icon) icon.setAttribute('aria-label', n > 0 ? `Wishlist, ${n} saved` : 'Wishlist');
  }

  // The store applies saves and removals optimistically, so the count moves
  // the moment a heart is tapped or Add All runs.
  if (window.MNWishlistStore) {
    window.MNWishlistStore.subscribe((items) => setSavedCount(items.length));
    window.MNWishlistStore.ready().then(() => setSavedCount(window.MNWishlistStore.count()));
  }

  // ---------------------------------------------------------------------------
  // Drawer
  // ---------------------------------------------------------------------------
  class WishlistDrawer extends HTMLElement {
    connectedCallback() {
      this.inner = this.querySelector('.wishlist-drawer__inner');
      this.itemsEl = this.querySelector('[data-wishlist-items]');
      this.statusEl = this.querySelector('[data-wishlist-status]');
      this.addAllBtn = this.querySelector('[data-wishlist-add-all]');
      this.loadSeq = 0;

      this.querySelectorAll('[data-wishlist-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape') this.close();
      });
      this.addAllBtn.addEventListener('click', () => this.addAll());
      this.itemsEl.addEventListener('click', (event) => this.onItemsClick(event));

      window.addEventListener('wishlist:items-removed', (event) => {
        const removed = new Set(((event.detail && event.detail.epis) || []).map(String));
        this.cards().forEach((card) => {
          if (removed.has(String(card.dataset.variantId))) this.removeCard(card);
        });
      });
    }

    cards() {
      return Array.from(this.itemsEl.querySelectorAll('.wishlist-card'));
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      clearTimeout(this.hideTimer);
      this.hidden = false;
      // Read layout once so the slide-in starts from the closed position. (Waiting
      // a frame instead stalls in a background tab, where frames don't run.)
      void this.inner.offsetWidth;
      this.classList.add('active');
      document.body.classList.add('overflow-hidden', 'drawer-open');
      // Focus once it has slid in; the timeout covers reduced motion, where no
      // transitionend fires.
      let focused = false;
      const focus = () => {
        if (focused || !this.classList.contains('active')) return;
        focused = true;
        if (typeof window.trapFocus === 'function') window.trapFocus(this.inner, this.querySelector('.wishlist-drawer__close'));
      };
      this.inner.addEventListener('transitionend', focus, { once: true });
      setTimeout(focus, 400);
      this.load();
    }

    close() {
      if (this.hidden) return;
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden', 'drawer-open');
      if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(this.opener);
      this.hideTimer = setTimeout(() => {
        if (!this.classList.contains('active')) this.hidden = true;
      }, 350);
      // On the cart page, an add made from here left the page's lines stale.
      if (window.MNWishlist && typeof window.MNWishlist.refreshCartPage === 'function') {
        window.MNWishlist.refreshCartPage();
      }
    }

    setStatus(message, withCartLink) {
      this.statusEl.textContent = message || '';
      if (message && withCartLink) {
        const viewCart = document.createElement('button');
        viewCart.type = 'button';
        viewCart.className = 'wishlist-drawer__status-cart';
        viewCart.textContent = 'View cart';
        viewCart.addEventListener('click', () => {
          this.close();
          window.MNWishlist.viewCart();
        });
        this.statusEl.append(' ', viewCart);
      }
      this.statusEl.hidden = !message;
    }

    syncFooter() {
      const buyable = this.itemsEl.querySelectorAll('.wishlist-card[data-available="true"]').length;
      this.classList.toggle('has-available', buyable > 0);
      this.addAllBtn.textContent = buyable > 1 ? `Add all to cart (${buyable})` : 'Add to cart';
    }

    async load() {
      const seq = ++this.loadSeq;
      this.setStatus('');
      this.classList.remove('has-available');
      this.itemsEl.innerHTML = '<div class="wishlist-drawer__loading" aria-hidden="true"></div>';
      this.inner.setAttribute('aria-busy', 'true');

      const store = window.MNWishlistStore;
      if (!store) {
        this.renderMessage("Your wishlist didn't load. Try again, or view it on the wishlist page.");
        return;
      }
      if (!(await store.ready(WISHLIST_TIMEOUT_MS))) {
        if (seq !== this.loadSeq) return;
        // Slow, or the first load failed: the store keeps retrying in the
        // background, so say so but fill in if the list lands while the drawer
        // is still open (the next open loads afresh anyway).
        this.renderMessage("Your wishlist is taking longer than usual to load. It'll show here as soon as it's ready.");
        await store.whenLoaded();
        if (!this.classList.contains('active')) return;
      }
      if (seq !== this.loadSeq) return;
      const saved = savedItems(store);
      if (!saved.length) {
        this.renderEmpty();
        return;
      }

      let templates;
      try {
        templates = await this.fetchCards([...new Set(saved.map((item) => String(item.productId)))]);
      } catch (e) {
        console.error('Wishlist drawer: cards failed to load', e);
        templates = null;
      }
      if (seq !== this.loadSeq) return;
      if (!templates) {
        this.renderMessage("Your wishlist didn't load. Try again, or view it on the wishlist page.");
        return;
      }

      const list = document.createElement('div');
      list.className = 'wishlist-drawer__list';
      saved.forEach((item) => {
        const template = templates.get(String(item.productId));
        const card = template ? template.cloneNode(true) : this.goneCard(item);
        card.dataset.itemId = item.itemId;
        if (template) this.fillCard(card, item);
        list.appendChild(card);
      });
      this.itemsEl.innerHTML = '';
      this.itemsEl.appendChild(list);
      this.inner.removeAttribute('aria-busy');
      if (typeof window.convertCurrency === 'function') window.convertCurrency(this.itemsEl);
      this.syncFooter();
    }

    // Product id -> card element, from the wishlist-slideout-card section.
    async fetchCards(productIds) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const batches = [];
      for (let i = 0; i < productIds.length; i += SEARCH_BATCH) batches.push(productIds.slice(i, i + SEARCH_BATCH));

      const pages = await Promise.all(
        batches.map(async (ids) => {
          const params = new URLSearchParams({
            section_id: 'wishlist-slideout-card',
            type: 'product',
            q: ids.map((id) => `id:${id}`).join(' OR '),
            'options[unavailable_products]': 'show',
            'options[prefix]': 'none',
          });
          const response = await fetch(`${root}search?${params}`);
          if (!response.ok) throw new Error(`wishlist cards ${response.status}`);
          return response.text();
        })
      );

      const templates = new Map();
      pages.forEach((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('.wishlist-card').forEach((card) => templates.set(card.dataset.productId, card));
      });
      return templates;
    }

    // Live variants baked into the card by snippets/wishlist-slideout-item.liquid.
    variantsOf(card) {
      try {
        return JSON.parse(card.querySelector('[data-wishlist-variants]').textContent);
      } catch (e) {
        return [];
      }
    }

    fillCard(card, item) {
      const variants = this.variantsOf(card);
      const variant = variants.find((v) => String(v.id) === String(item.variantId));
      const size = card.dataset.hasSizes === 'true' && variant ? formatSize(variant.title) : '';
      card.dataset.variantId = item.variantId || '';

      const name = card.querySelector('.wishlist-card__name').textContent.trim();
      const subtitle = card.querySelector('.wishlist-card__subtitle');
      card.dataset.label = [subtitle ? `${name} - ${subtitle.textContent.trim()}` : name, size].filter(Boolean).join(', ');

      const sizeEl = card.querySelector('[data-wishlist-variant]');
      if (size) {
        sizeEl.textContent = size;
        sizeEl.hidden = false;
      }

      const priceEl = card.querySelector('[data-wishlist-price]');
      if (variant) {
        priceEl.innerHTML = variant.compare
          ? `<span class="money wishlist-card__price-sale">${variant.price}</span> <s class="money wishlist-card__price-compare">${variant.compare}</s>`
          : `<span class="money">${variant.price}</span>`;
      }

      let buyable = false;
      const atc = card.querySelector('[data-wishlist-atc]');
      if (card.dataset.mode === 'buy' && atc) {
        if (variant && variant.available) {
          atc.disabled = false;
          buyable = true;
        } else if (!item.variantId) {
          // Saved without a size (product-level save): send the shopper to pick one.
          this.markNeedsSize(card);
        } else {
          this.markSoldOut(card, variants.some((v) => v.available));
        }
      }
      card.dataset.available = String(buyable);
    }

    markSoldOut(card, otherSizesInStock) {
      const atc = card.querySelector('[data-wishlist-atc]');
      if (atc) {
        atc.disabled = true;
        atc.dataset.type = 'sold-out';
        atc.textContent = (window.variantStrings && window.variantStrings.soldOut) || 'Sold out';
      }
      const otherSizes = card.querySelector('[data-wishlist-other-sizes]');
      if (otherSizes) otherSizes.hidden = !otherSizesInStock;
      card.dataset.available = 'false';
    }

    markNeedsSize(card) {
      const atc = card.querySelector('[data-wishlist-atc]');
      if (atc) atc.hidden = true;
      const otherSizes = card.querySelector('[data-wishlist-other-sizes]');
      if (otherSizes) {
        otherSizes.textContent = 'Choose a size';
        otherSizes.hidden = false;
      }
      card.dataset.available = 'false';
    }

    // A saved product the search didn't return (deleted, or hidden from
    // search): shown so it can still be removed. Swish items carry only ids, so
    // the name and photo come from /products/<handle>.js when the product still
    // has a page.
    goneCard(item) {
      const card = document.createElement('article');
      card.className = 'wishlist-card wishlist-card--gone';
      card.dataset.available = 'false';
      card.dataset.variantId = item.variantId || '';
      card.innerHTML = `
        <div class="wishlist-card__media"></div>
        <div class="wishlist-card__details">
          <p class="wishlist-card__name"></p>
          <p class="wishlist-card__subtitle">No longer available</p>
        </div>
        <button type="button" class="wishlist-card__remove" data-wishlist-remove aria-label="Remove from wishlist">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 17" width="12" height="12" fill="none" aria-hidden="true" focusable="false"><path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.985 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor"/></svg>
        </button>`;
      if (item.handle) this.fillGoneCard(card, item.handle);
      return card;
    }

    async fillGoneCard(card, handle) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      try {
        const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
        if (!response.ok) return;
        const product = await response.json();
        card.querySelector('.wishlist-card__name').textContent = (product.title || '').split(' - ')[0];
        if (product.featured_image) {
          const img = document.createElement('img');
          img.src = product.featured_image;
          img.alt = '';
          img.width = 80;
          img.loading = 'lazy';
          img.className = 'wishlist-card__img';
          card.querySelector('.wishlist-card__media').appendChild(img);
        }
      } catch (e) {
        // The card still lets the shopper remove it.
      }
    }

    onItemsClick(event) {
      const atc = event.target.closest('[data-wishlist-atc]');
      if (atc && !atc.disabled) {
        this.addCards([atc.closest('.wishlist-card')], atc);
        return;
      }
      const remove = event.target.closest('[data-wishlist-remove]');
      if (remove) this.removeSaved(remove.closest('.wishlist-card'), remove);
    }

    addAll() {
      this.addCards(Array.from(this.itemsEl.querySelectorAll('.wishlist-card[data-available="true"]')), this.addAllBtn);
    }

    async addCards(cards, button) {
      if (!cards.length || !window.MNWishlist || button.classList.contains('loading')) return;
      this.setStatus('');
      button.classList.add('loading');
      button.disabled = true;

      const result = await window.MNWishlist.addAllToCart(
        cards.map((card) => ({ epi: card.dataset.variantId, pid: card.dataset.productId, label: card.dataset.label }))
      );

      button.classList.remove('loading');
      button.disabled = false;
      // Cards Shopify added are removed by the wishlist:items-removed listener.
      result.failed.forEach((entry) => {
        const card = cards.find((c) => String(c.dataset.variantId) === String(entry.epi));
        if (!card) return;
        const otherSizes = this.variantsOf(card).some((v) => v.available && String(v.id) !== String(entry.epi));
        this.markSoldOut(card, otherSizes);
      });
      this.syncFooter();

      const message = window.MNWishlist.describe(result);
      if (!message) {
        this.close();
        window.MNWishlist.showCart(result.cart);
        return;
      }
      if (result.added.length) window.MNWishlist.showCart(result.cart, { open: false });
      this.setStatus(message, result.added.length > 0);
    }

    async removeSaved(card, button) {
      if (!card || !card.dataset.itemId || !window.MNWishlistStore) return;
      button.classList.add('loading');
      try {
        await window.MNWishlistStore.remove(card.dataset.itemId);
        this.removeCard(card);
      } catch (e) {
        console.error('Wishlist drawer: remove failed', e);
        button.classList.remove('loading');
      }
    }

    removeCard(card) {
      if (card.classList.contains('is-removing')) return;
      card.classList.add('is-removing');
      // transitionend never fires when the drawer is hidden, so don't rely on it.
      setTimeout(() => {
        card.remove();
        this.syncFooter();
        if (!this.cards().length) this.renderEmpty();
      }, 260);
    }

    renderEmpty() {
      this.classList.remove('has-available');
      this.inner.removeAttribute('aria-busy');
      this.itemsEl.innerHTML = `
        <div class="wishlist-drawer__empty">
          <p class="wishlist-drawer__empty-title">Your wishlist is empty</p>
          <p>Tap the heart on anything you like and it'll be saved here.</p>
          <div class="wishlist-drawer__empty-buttons">
            <a class="button button--contained button--full-width" href="/collections/womens">Shop womens</a>
            <a class="button button--contained button--full-width" href="/collections/mens">Shop mens</a>
          </div>
        </div>`;
    }

    renderMessage(text) {
      this.classList.remove('has-available');
      this.inner.removeAttribute('aria-busy');
      this.itemsEl.innerHTML = `<p class="wishlist-drawer__message">${escapeHTML(text)}</p>`;
    }
  }

  customElements.define('wishlist-drawer', WishlistDrawer);

  // The heart is a plain link to /pages/wishlist. Open the drawer instead, unless
  // we're already on that page or the shopper wants a new tab.
  document.addEventListener('click', (event) => {
    const icon = event.target.closest('#wishlist-icon-bubble');
    if (!icon || isWishlistPage()) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const drawer = document.querySelector('wishlist-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(icon);
  });
})();
